/* eslint-disable max-lines */
import React, { FC } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import loadable from '@loadable/component';
import { body3, caption1 } from '@westwing/ui-kit/typography';
import { forDesktop, forIE, isDesktop } from '@westwing/ui-kit/breakPoints';
import { SessionTypes } from '../../../client/redux/user/types';
import { remoteLoginEvent } from '../../../common/events/service';
import useTranslator from '../../../client/hooks/useTranslator';
import UserIcon from '../../../client/components/Generic/Icons/Svgs/v2/User';
import MoodboardIcon from '../../../client/components/Generic/Icons/Svgs/v2/MoodBoard';
import WishlistHeaderIcon from '../../../client/components/Generic/Icons/Svgs/v2/Wishlist';
import WishlistHeaderHighligthedIcon from '../../../client/components/Generic/Icons/Svgs/v2/WishlistHighlighted';
import CartIcon from '../../../client/components/Generic/Icons/Svgs/v2/Cart';
import StateInterface from '../../../client/redux/types';
import { hideOverlay, showOverlay } from '../../../client/redux/overlays/actions';
import { setRedirectUrl } from '../../../client/redux/user/actions';
import { OverlayId } from '../../../client/components/Overlays/types';
import { useOverlayStateChecker } from '../../../client/components/Overlays/useOverlayStateChecker';
import { fetchCartData } from '../../../client/redux/cart/actions';
import { CartProductItem } from '../../../client/redux/cart/types';
import useBellaPush from '../../../client/hooks/useBellaHistoryPush';
import useConfig from '../../../client/hooks/useConfig';
import HybridLink from '../../../client/components/Generic/Links/HybridLink';
import { VictorQaClasses, BellaTestIds } from '../../../common/qaIds';
import { CartFlyoutTimeoutKey } from '../../../common/events';
import useGa from '../../../client/hooks/useGa';
import { TrackHeaderActions } from '../../../client/ga/types';
import useRouter from '../../../client/hooks/useRouter';
import { WishListUrlList } from '../../../client/hooks/UrlManager/types';
import { usePageResolver } from '../../../client/hooks/useTrackingPageLabel';
import CountBubble from './CountBubble';
import AccountContent from './AccountContent';
import CartOverlay from './CartOverlay';

const ContentOverlay = loadable(() => import('../../../client/components/Overlays/ContentOverlay'));

const MAX_USER_NAME_LENGTH = 10;
const ACCOUNT_RIGHT_POSITION = 18;
const Icons = styled.div`
    grid-area: icons;
    display: grid;
    grid-template-columns: repeat(2, 40px);
    align-items: center;
    justify-content: space-around;

    padding-top: 6px;
    padding-left: 6px;
    padding-right: 26px;

    ${forDesktop`
        grid-template-columns: 89px 85px 81px;
        grid-template-columns: repeat(4, 1fr);
        align-items: self-start;
        padding: 24px 10px 0 0;
        justify-content: end;
    `}
    ${forIE`
        -ms-grid-row: 2;
        -ms-grid-column: 8;
        -ms-grid-column-span: 1;
        display: flex;
    `}
`;

enum IconScaleFactors {
    small = '1',
    medium = '36/30',
}

const IconStyles = css`
    height: 36px;
    width: 31px;
    color: ${({ theme }) => theme.colors.charcoal100};
    text-align: center;
    ${forDesktop`
        fill: ${({ theme }) => theme.colors.charcoal100};
        transform: scale(calc(${IconScaleFactors.medium}));
    `}
`;

const MenuIconWrapper = styled.span`
    position: relative;
    display: inline-block;
    ${IconStyles}
`;

const MenuLabel = styled.span`
    ${caption1}
    display: none;
    text-align: center;
    color: ${({ theme }) => theme.colors.charcoal100};
    ${forDesktop`
        display: block;
    `}
    ${forIE`
        max-width: 88px;
    `}
`;

const MenuSubLabel = styled(MenuLabel)`
    ${body3}
    font-size: 12px;
    color: ${({ theme }) => theme.colors.turquoise100};
`;

const ItemWrapper = styled.div`
    display: flex;
    flex-flow: column;
    align-items: center;
    ${forIE`
        padding: 0 5px;
    `}
    :hover
        ${MenuLabel} {
        opacity: 0.5;
    }
`;

const DesktopOnlyItemWrapper = styled(ItemWrapper)`
    display: none;
    ${forDesktop`
        display: flex;
    `}
`;

const IconItemWrapper = styled.div`
    display: block;
    text-align: center;
    /* width: 30px; */
    ${forDesktop`
        cursor: pointer;
        width: auto;
    `}
`;

const IconLink = styled(HybridLink)`
    text-align: left;
    display: flex;
    flex-flow: column;
    align-items: center;
`;

export const getUserDisplayName = (firstName: string, email = '', isUserLoggedIn: boolean) => {
    const defaultName = firstName || email || '';
    const accountName = isUserLoggedIn ? defaultName : '';

    return accountName.length > MAX_USER_NAME_LENGTH ? `${accountName.substring(0, 7)}...` : accountName;
};

interface Props {
    cartCount: number;
    wishListItemCount: number;
    wishListUrl: string;
    myAccountUrl: string;
    cartUrl: string;
    carts: CartProductItem[];
    subtotal?: string;
    email?: string;
    firstName?: string;
}

const IconsButtonTray: FC<Props> = ({
    wishListItemCount,
    cartCount,
    wishListUrl,
    cartUrl,
    myAccountUrl,
    subtotal = '',
    carts,
    email,
    firstName = '',
}) => {
    const t = useTranslator();
    const dispatch = useDispatch();
    const bellaPush = useBellaPush();
    const config = useConfig();
    const {
        location: { pathname },
    } = useRouter();

    const { countryCode, currency, links } = config;

    const isUserLoggedIn = useSelector<StateInterface, boolean>(state => state.login.isLoggedIn);

    const isUserFullyLoggedIn = useSelector<StateInterface, boolean>(
        state => state.login.isLoggedIn && !!(state.login.data && state.login.data.sessionType === SessionTypes.full)
    );

    const isPartiallyLoggedIn = useSelector<StateInterface, boolean>(
        state => state.login.isLoggedIn && !!(state.login.data && state.login.data.sessionType === SessionTypes.partial)
    );

    const accountName = getUserDisplayName(firstName, email, isUserLoggedIn);
    const ga = useGa();
    const pageTypeLabel = usePageResolver();
    const overlayState = useOverlayStateChecker();
    const isOverlayActive = (id: OverlayId): boolean => {
        const overlay = overlayState(id);
        return overlay && overlay.isActive;
    };

    const remoteLogin = remoteLoginEvent();

    const handleAccountClick = () => {
        ga.oneHeader.trackHeaderAction('accounticonclick', TrackHeaderActions.HeaderMyAccountClick);
        if (isUserFullyLoggedIn && isDesktop()) {
            bellaPush(myAccountUrl);
            return;
        }
        dispatch(setRedirectUrl(myAccountUrl));
        dispatch(showOverlay(OverlayId.LOGIN_AND_REGISTER));
        remoteLogin({ redirectUrl: myAccountUrl, email });
    };

    const handleWishListClick = () => {
        ga.oneHeader.trackHeaderAction('wishlisticonclick', TrackHeaderActions.HeaderWishlistClick);

        if (!isUserLoggedIn || isPartiallyLoggedIn) {
            dispatch(setRedirectUrl(wishListUrl));
            dispatch(showOverlay(OverlayId.LOGIN_AND_REGISTER, { isLogin: isPartiallyLoggedIn }));
            remoteLogin({ redirectUrl: wishListUrl, email });
            return;
        }
        bellaPush(wishListUrl);
    };

    const handleCartClick = () => {
        ga.oneHeader.trackHeaderAction('carticonclick', TrackHeaderActions.HeaderCartClick);
    };

    const handleMoodboardClick = () => {
        ga.oneHeader.trackHeaderAction('moodboardiconclick', TrackHeaderActions.HeaderMoodboardClick, pageTypeLabel);
    };

    const handleItemHover = (overlayId: string) => {
        if (overlayId === OverlayId.DESKTOP_CART_OVERLAY) {
            window.clearTimeout(window[CartFlyoutTimeoutKey]);
            if (cartCount > 0) {
                dispatch(fetchCartData({ countryCode, currency }));
                dispatch(showOverlay(overlayId));
            }
            return;
        }

        if (isUserLoggedIn) {
            dispatch(showOverlay(overlayId));
        }

        if (isOverlayActive(OverlayId.DESKTOP_NAV_FLYOUT)) {
            dispatch(hideOverlay(OverlayId.DESKTOP_NAV_FLYOUT));
        }
    };

    const closeActiveOverlay = (id: OverlayId) => {
        if (isOverlayActive(id)) {
            dispatch(hideOverlay(id));
        }
    };
    const accountIconClassname = isUserLoggedIn ? VictorQaClasses.headerAccountLink : VictorQaClasses.headerSignUpLink;

    return (
        <Icons>
            <DesktopOnlyItemWrapper
                onMouseEnter={() => handleItemHover(OverlayId.DESKTOP_ACCOUNT_OVERLAY)}
                onMouseLeave={() => closeActiveOverlay(OverlayId.DESKTOP_ACCOUNT_OVERLAY)}
                data-testid="one-header-icon-my-account"
            >
                <IconItemWrapper className={accountIconClassname} onClick={handleAccountClick}>
                    <MenuIconWrapper>
                        <UserIcon />
                    </MenuIconWrapper>
                    <MenuLabel>{t('My account')}</MenuLabel>
                    <MenuSubLabel>{accountName}</MenuSubLabel>
                </IconItemWrapper>
                <ContentOverlay
                    identifier={OverlayId.DESKTOP_ACCOUNT_OVERLAY}
                    rightPosition={ACCOUNT_RIGHT_POSITION}
                    propagateOutsideClicks
                >
                    <AccountContent />
                </ContentOverlay>
            </DesktopOnlyItemWrapper>
            <DesktopOnlyItemWrapper data-testid="one-header-icon-moodboard" onClick={handleMoodboardClick}>
                <IconLink to={links.moodboardLandingPage}>
                    <MenuIconWrapper>
                        <MoodboardIcon />
                    </MenuIconWrapper>
                    <MenuLabel>{t('moodboard_desktopheader')}</MenuLabel>
                </IconLink>
            </DesktopOnlyItemWrapper>
            <ItemWrapper
                className={`WishlistCount ${VictorQaClasses.headerWishlistLink}`}
                onClick={handleWishListClick}
                data-testid="wishlist-bubble"
            >
                <IconItemWrapper>
                    <MenuIconWrapper>
                        {!isDesktop() && WishListUrlList.includes(pathname) ? (
                            <WishlistHeaderHighligthedIcon className="icon-wishlist" />
                        ) : (
                            <WishlistHeaderIcon className="icon-wishlist" />
                        )}
                        {isUserLoggedIn && wishListItemCount > 0 && (
                            <CountBubble
                                count={wishListItemCount}
                                counterTestId="wishlist-counter"
                                className={VictorQaClasses.headerWishlistCounter}
                            />
                        )}
                    </MenuIconWrapper>
                    <MenuLabel>{t('Wishlist_Desktop')}</MenuLabel>
                </IconItemWrapper>
            </ItemWrapper>
            <ItemWrapper
                onMouseLeave={() => closeActiveOverlay(OverlayId.DESKTOP_CART_OVERLAY)}
                onClick={handleCartClick}
                data-testid="one-header-icon-cart"
            >
                <IconLink
                    to={cartUrl}
                    onMouseEnter={() => handleItemHover(OverlayId.DESKTOP_CART_OVERLAY)}
                    className={VictorQaClasses.headerCartLink}
                >
                    <MenuIconWrapper>
                        <CartIcon />
                        {cartCount > 0 && (
                            <CountBubble
                                count={cartCount}
                                counterTestId={BellaTestIds.headerCartCounter}
                                className={VictorQaClasses.headerCartCounter}
                            />
                        )}
                    </MenuIconWrapper>
                    <MenuLabel>{t('Cart')}</MenuLabel>
                </IconLink>
                <CartOverlay carts={carts} subtotal={subtotal} cartUrl={cartUrl} cartCount={cartCount} />
            </ItemWrapper>
        </Icons>
    );
};

const mapStateToProps = (state: StateInterface) => {
    const { count: cartCount, carts, subtotal } = state.cart;
    if (state.login.data) {
        const { email, firstName } = state.login.data;
        return { cartCount, carts, subtotal, email, firstName };
    }
    return { cartCount, carts, subtotal, email: '', firstName: '' };
};

export default connect(mapStateToProps)(IconsButtonTray);
